// The spring maths every settle in the app is built from — a small pure module, no DOM and no engine.
// Read-back is from the animation's OWN clock, never computed style, so a caught/re-grabbed glide always
// agrees with the pixels on screen.

import { TRANSITION_CONFIG } from './motion.config'

const { spring } = TRANSITION_CONFIG

/**
 * How finely the spring is SAMPLED into keyframes — not a frame rate, and not a cap on one. Between two
 * samples the compositor interpolates a straight line, so sampling at 60Hz would hand a 120Hz display
 * piecewise-linear corners at exactly the rate it can resolve. 240Hz stays finer than any shipping panel,
 * and the cost is nothing (a ~350ms settle is ~84 keyframes).
 */
export const SPRING_HZ = 240
/** Hard cap on a settle (3s at SPRING_HZ) — a spring that hasn't arrived by then never will. */
const SPRING_MAX_STEPS = SPRING_HZ * 3

/**
 * How close to the target counts as arrived: 1% of the travel, ≈ a few px.
 *
 * Settle on POSITION, not velocity. The tail of a near-critically-damped spring can hover with tiny residual
 * velocity for hundreds of ms, and for a REVERSE (target 0) the top pane sits AT the covering position for
 * that whole time — which reads as a frozen screen, not as a soft landing. Lives here, with the sampler and
 * the integrator that both break on it, so the composited path and the main-thread fallback can only ever
 * stop at the same place.
 */
export const SETTLE_EPSILON = 0.01

/** One step of the spring, shared by the ahead-of-time sampler and the main-thread fallback integrator. */
export function springStep(progress: number, velocity: number, target: number, dt: number): SpringState {
  const accel = (-spring.stiffness * (progress - target) - spring.damping * velocity) / spring.mass
  const v = velocity + accel * dt
  return { progress: progress + v * dt, velocity: v }
}

export interface SpringState {
  readonly progress: number
  readonly velocity: number
}

/** Which sample a given elapsed time lands on, clamped into the array. One definition so a trajectory's
 *  POSITION and its VELOCITY can never be read off different indices. */
export function sampleIndexAt(samples: readonly number[], elapsedMs: number): number {
  const index = Math.round((elapsedMs / 1000) * SPRING_HZ)
  return index < 0 ? 0 : index > samples.length - 1 ? samples.length - 1 : index
}

/**
 * Integrate the spring AHEAD of time, into the progress it will hold on each frame. A release/glide is not
 * interactive — its whole trajectory is determined the moment it starts — so it can be handed to the
 * compositor as keyframes, where a concurrent React commit CANNOT starve it. Sampled keyframes rather than a
 * `linear()` spring easing on purpose: `linear()` is too new for some browsers this app still ships to;
 * linearly-interpolated keyframes work everywhere `Element.animate` does.
 */
export function springSamples(from: number, target: number, velocity: number): number[] {
  const dt = 1 / SPRING_HZ
  const samples: number[] = [from]
  let p = from
  let v = velocity
  for (let i = 0; i < SPRING_MAX_STEPS; i++) {
    ;({ progress: p, velocity: v } = springStep(p, v, target, dt))
    if (Math.abs(p - target) < SETTLE_EPSILON) break
    samples.push(p)
  }
  samples.push(target)
  return samples
}

/**
 * The app's canonical 0 → 1 spring trajectory, integrated ONCE at module load and shared by the surface
 * engine's link glide, the nav stack's settle and the bottom sheet's travel. One array is what guarantees
 * they move on the SAME curve; each still time-scales it into its own duration.
 */
export const SPRING_TRAJECTORY: readonly number[] = springSamples(0, 1, 0)

/**
 * Where a composited spring animation has actually got to — read back from the animation's OWN clock
 * (`currentTime` → sample index), never from computed style: one source of truth, no layout read, so a
 * re-grab/catch hands a baseline that exactly matches the pixels. `fallback` covers the moments before/after
 * an animation exists.
 */
export function springProgressAt(lead: Animation | undefined, samples: readonly number[], fallback: number): number {
  if (!lead || samples.length === 0) return fallback
  return samples[sampleIndexAt(samples, Number(lead.currentTime ?? 0))] ?? fallback
}
